import styled from 'styled-components';
import MuiButton from '@mui/material/Button';
import Theme from '../../StyledComponentThemes';

interface IButtonProps {
    margin?: string;
    disabled?: boolean;
    small?: boolean;
    padding?: string;
    large?: boolean;
    tiny?: boolean;
    variant?: string;
}

// Standard button
// Blue bkg, white text
export const Button = styled.button<IButtonProps>`
    display: inline-block;
    margin: ${p => (p.margin ? p.margin : '0 10px 0 0')};
    background-color: ${p => (p.disabled ? '#f0f0f0' : Theme.blue)};
    font-family: ${Theme.fontDefault};
    color: ${p => (p.disabled ? '#A4AFB7' : '#fff')};
    transition: 0.2s ease-out all;
    text-transform: none;
    box-shadow: none;
    letter-spacing: 0;
    font-size: ${p => (p.small ? '11px' : '12px')};
    border: 1px solid ${p => (p.disabled ? '#f0f0f0' : Theme.blue)};
    border-radius: 2px;
    line-height: 1;
    height: ${p => (p.small ? '23px' : '26px')};
    padding: ${p => (p.padding ? p.padding : '0 1rem')};
    font-weight: 600;
    white-space: nowrap;
    ${p =>
        p.small &&
        `
        font-size: 12px;
        line-height: 17px;
        height: 25px;
        padding: ${p.padding ? p.padding : '0 10px'};
    `}
    ${p =>
        p.tiny &&
        `
        font-size: 12px;
        line-height: 12px;
        height: 20px;
        font-weight: 400;
        padding: ${p.padding ? p.padding : '0 8px'};
    `}
    ${p =>
        p.large &&
        `
        font-size: 14px;
        height: 36px;
        padding: ${p.padding ? p.padding : '0 20px'}
    `}
    ${p =>
        !p.disabled &&
        `&:hover,
    &:focus,
    &:active {
        background-color: ${Theme.blueHover};
        border-color: ${Theme.blueHover};
        box-shadow: 0 1px 2px rgba(0,0,0,.15);
        color: #fff;
        cursor: pointer;
    }
    &:active {
        box-shadow: none;
    }`}
    ${p =>
        p.disabled &&
        `
    &, &:hover, &:focus, &:active {
        color: #ccc;
        border-color: #ccc;
    }`}
    ${p =>
        p.variant === 'contained' &&
        `
        background-color: ${Theme.blue};
        color: white;
        border: none;
    `}
    ${p =>
        p.variant === 'outlined' &&
        `
        background-color: transparent;
        color: ${Theme.blue};
        border: 1px solid ${Theme.blue};
    `}
`;

// red button
export const ButtonRed = styled(Button)`
    background-color: ${Theme.red};
    border-color: ${Theme.red};
    &:hover,
    &:focus,
    &:active {
        background-color: ${Theme.redHover};
        border-color: ${Theme.redHover};
    }
`;
export const MuiButtonRed = styled(MuiButton)`
    background-color: ${Theme.red};
    border-color: ${Theme.red};
    &:hover,
    &:focus,
    &:active {
        background-color: ${Theme.redHover};
        border-color: ${Theme.redHover};
    }
`;

// green button
export const ButtonGreen = styled(Button)`
    background-color: ${Theme.green};
    border-color: ${Theme.green};
    &:hover,
    &:focus,
    &:active {
        background-color: ${Theme.greenHover};
        border-color: ${Theme.greenHover};
    }
`;
export const MuiButtonGreen = styled(MuiButton)`
    background-color: ${Theme.green};
    border-color: ${Theme.green};
    &:hover,
    &:focus,
    &:active {
        background-color: ${Theme.greenHover};
        border-color: ${Theme.greenHover};
    }
`;

// orange button
export const ButtonOrange = styled(Button)`
    background-color: ${Theme.orange};
    border-color: ${Theme.orange};
    &:hover,
    &:focus,
    &:active {
        background-color: ${Theme.orangeHover};
        border-color: ${Theme.orangeHover};
    }
`;

// Secondary button
// Blue border, transparent background, blue text
export const ButtonSecondary = styled(Button)`
    background: transparent;
    color: ${Theme.blue};
    &:hover,
    &:focus,
    &:active {
        background: transparent;
        color: ${Theme.blueHover};
    }
    ${p =>
        p.disabled &&
        `
        &, &:hover, &:focus, &:active {
            color: #ccc;
            border-color: #ccc;
        }
    `}
`;

// Secondary button
// red border, transparent background, red text
export const ButtonSecondaryRed = styled(Button)`
    background: transparent;
    border-color: ${Theme.red};
    color: ${Theme.red};
    &:hover,
    &:focus,
    &:active {
        background: transparent;
        color: ${Theme.redHover};
        border-color: ${Theme.redHover};
    }
`;

// Secondary button
// Blue border, transparent background, blue text
export const ButtonSecondaryGray = styled(ButtonSecondary)`
    color: #999;
    border-color: #999;
    &:hover,
    &:focus,
    &:active {
        color: #b0b0b0;
        border-color: #b0b0b0;
    }
`;

// Secondary button white
// White border, transparent background, white text
export const ButtonSecondaryWhite = styled(ButtonSecondary)`
    color: #fff;
    border-color: #fff;
    &:hover,
    &:focus,
    &:active {
        cursor: pointer;
        color: rgba(255, 255, 255, 0.9);
        border-color: rgba(255, 255, 255, 0.9);
        box-shadow: none;
    }
`;

// Secondary button white text
// Blue border, transparent background, white text
export const ButtonSecondaryWhiteText = styled(ButtonSecondary)`
    color: #fff;
    &:hover,
    &:focus,
    &:active {
        color: #fff;
        cursor: pointer;
        box-shadow: none;
    }
`;

// Small version of secondary button like min/max on flights
export const SmallButtonSecondary = styled(ButtonSecondary)`
    &,
    &:hover,
    &:active,
    &:focus {
        height: 18px;
        line-height: 16px;
        border-width: 1px;
        font-size: 11px;
        padding: 0 4px;
    }
    &:focus {
        box-shadow: none;
    }
`;

interface IButtonTextProps {
    gray?: boolean;
    red?: boolean;
    bold?: boolean;
}

// Text button
// no button chrome, just text
export const ButtonText = styled(ButtonSecondary)<IButtonTextProps>`
    border-color: transparent;
    color: ${Theme.blue};
    ${p => p.gray && 'color: rgba(106, 121, 134, 0.74)'}
    ${p => p.red && `color: ${Theme.red};`}
    font-weight: ${p => (p.bold ? '600' : '400')};
    transition: ease 0.2s color;
    padding: ${p => (p.padding ? p.padding : '0')};
    &:hover,
    &:focus,
    &:active {
        border-color: transparent;
        color: ${Theme.blueHover};
        ${p => p.gray && 'color: rgba(106, 121, 134, 1)'}
        ${p => p.red && `color: ${Theme.redHover};`}
        box-shadow: none;
    }
`;

interface IInlineFormButtonProps {
    isActive?: boolean;
    padding?: string;
    margin?: string;
    flex?: string;
}

// Flights form buttons ie(route advisor, altitude advisor, etc.)
export const InlineFormButton = styled.button<IInlineFormButtonProps>`
    display: flex;
    align-items: center;
    position: relative;
    transition: all 0.3s ease;
    opacity: ${p => (p.isActive ? '1' : '.5')};
    border-radius: 2px;
    padding: ${p => (p.padding ? p.padding : '4px 5px')};
    margin: ${p => (p.margin ? p.margin : '0')};
    height: 22px;
    background: ${p =>
        p.isActive ? 'rgba(216,216,216,.44)' : 'rgba(216,216,216,.34)'};
    box-shadow: ${p => (p.isActive ? '0 1px 1px rgba(0,0,0,.05)' : 'none')};
    border: 1px solid rgba(211, 211, 211, 0.4);
    color: ${p => (p.isActive ? Theme.defaultGray : '#a8a8a8')};
    text-transform: capitalize;
    font-size: 11px;
    pointer-events: ${p => (p.isActive ? 'all' : 'none')};
    white-space: nowrap;
    ${p => p.flex && `flex: ${p.flex};`}
    &:hover {
        cursor: pointer;
        background: rgba(216, 216, 216, 0.34);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    &:active {
        background: rgba(216, 216, 216, 0.54);
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2) inset;
    }
    &:focus {
        background: rgba(216, 216, 216, 0.54);
        box-shadow: none;
    }
`;

interface IHeaderButtonProps {
    background?: string;
}

// Light buttons on dark backgrounds i.e. Aircraft page header
export const HeaderButton = styled(Button)<IHeaderButtonProps>`
    height: 24px;
    line-height: 1;
    font-size: 11px;
    font-weight: 400;
    margin: 0 0 0 8px;
    color: #fff;
    border: none;
    border-image: none;
    border-radius: 2px;
    background: ${p => (p.background ? p.background : 'rgba(255,255,255,.2)')};
    &:active,
    &:focus {
        background: ${p =>
            p.background ? p.background : 'rgba(255,255,255,.2)'};
        box-shadow: none;
        border: none;
    }
    &:hover {
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
        background: ${p =>
            p.background ? p.background : 'rgba(255,255,255,.3)'};
        border: none;
    }
`;

export const ButtonGroup = styled.ul`
    display: flex;
`;

interface IMapButtonProps {
    $isActive?: boolean;
}

export const MapButton = styled.button<IMapButtonProps>(
    ({ $isActive }) => `
    height: 30px;
    min-width: 30px;
    background-color: ${$isActive ? Theme.blue : '#fff'};
    position: relative;
    border-radius: 2px;
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
    border: none;
    transition: box-shadow ease 0.2s;
    pointer-events: all;
    display: flex;
    justify-content: center;
    align-items: center;
    ${$isActive ? 'color: white;' : ''}
    
    &:hover,
    &:active,
    &:focus {
        border-color: #c7c8ca;
        background-color: ${$isActive ? Theme.blue : '#fff'};
        box-shadow: 0 2px 3px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
        box-shadow: none;
    }
`
);

MapButton.defaultProps = {
    type: 'button',
};

interface IMapButtonSmallProps {
    margin?: string;
}

export const MapButtonSmall = styled(MapButton)<IMapButtonSmallProps>`
    height: 30px;
    width: 30px;
    min-width: auto;
    margin: 14px;
    border-radius: 0;
    svg {
        width: 100%;
    }
`;

MapButtonSmall.defaultProps = {
    type: 'button',
};

export const MapButtonGroup = styled.div`
    ${MapButtonSmall} {
        margin: 0 14px 1px;
        &:first-child {
            border-radius: 2px 2px 0 0;
        }
        &:last-child {
            border-radius: 0 0 2px 2px;
            margin: 0 14px 14px;
        }
    }
`;
